import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '@/api/client';
import { StatusBadge } from '@/components/StatusBadge';
import { LoadingState } from '@/components/Spinner';
import type { SessionStatus } from '@/types/session';

function SourceBadge({ source }: { source: string }) {
  const config: Record<string, { label: string; cls: string }> = {
    default: { label: 'デフォルト', cls: 'bg-blue-100 text-blue-700' },
    manual: { label: '手動追加', cls: 'bg-green-100 text-green-700' },
    admin: { label: '管理者設定', cls: 'bg-orange-100 text-orange-700' },
  };
  const c = config[source] ?? { label: source, cls: 'bg-gray-100 text-gray-600' };
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${c.cls}`}>{c.label}</span>;
}

interface ClientDetail {
  id: string; name: string; owner_coach_id: string; coach_name: string;
  sessions: Array<{ id: string; day_type: string; serial_no: number; status: SessionStatus; outcome: string | null; session_date: string | null; updated_at: string; requires_my_action: boolean }>;
  feedbackers: Array<{ user_id: string; user_name: string; email: string; assignment_source: string }>;
}

export function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<ClientDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/clients/${clientId}`).then(r => { setData(r.data); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [clientId]);

  if (loading) return <div className="p-8 flex justify-center"><LoadingState /></div>;
  if (error) return <div className="p-8 text-center text-red-500">{error}</div>;
  if (!data) return null;

  return (
    <div>
      <div className="mb-4"><Link to="/clients" className="text-sm text-blue-600 hover:underline">← クライアント一覧</Link></div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">{data.name}</h1>
          <p className="mt-1 text-sm text-gray-500">担当コーチ: {data.coach_name}</p>
        </div>
        <button type="button" onClick={() => navigate(`/clients/${clientId}/upload`)}
          className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">+ 新規セッション追加</button>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-6">
        <div className="col-span-2">
          <h2 className="text-sm font-semibold text-gray-700">セッション一覧（{data.sessions.length}件）</h2>
          <div className="mt-2 overflow-hidden rounded-lg bg-white shadow-sm">
            {data.sessions.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-400">セッションはまだありません</div>
            ) : (
              <table className="w-full">
                <thead className="border-b bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs text-gray-500">実施日</th>
                    <th className="px-4 py-2 text-left text-xs text-gray-500">Day</th>
                    <th className="px-4 py-2 text-left text-xs text-gray-500">ステータス</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.sessions.map(s => (
                    <tr
                      key={s.id}
                      onClick={() => navigate(`/sessions/${s.id}/feedback`)}
                      className={`cursor-pointer ${
                        s.requires_my_action
                          ? 'bg-red-50 font-semibold hover:bg-red-100'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className="px-4 py-3 text-sm text-gray-700">{s.session_date ?? '—'}</td>
                      <td className="px-4 py-3 text-sm">{s.day_type}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-2">
                          <StatusBadge status={s.status} />
                          {s.requires_my_action && <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">要対応</span>}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="col-span-1">
          <h2 className="text-sm font-semibold text-gray-700">フィードバッカー（{data.feedbackers.length}名）</h2>
          <div className="mt-2 space-y-2">
            {data.feedbackers.map(fb => (
              <div key={fb.user_id} className="rounded-lg bg-white p-3 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{fb.user_name}</p>
                    <p className="text-xs text-gray-400">{fb.email}</p>
                  </div>
                  <SourceBadge source={fb.assignment_source} />
                </div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-gray-400">過去セッションの割当は変更されません</p>
        </div>
      </div>
    </div>
  );
}
